import {
  Alert, AlertIcon, Badge, Box, Button, FormControl, FormLabel, HStack, Switch, Text, VStack, useToast,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { ConfirmDialog, DataTable, PageContainer } from '@/design-system/components';
import { useAuth } from '@/lib/sdk/auth/hooks';
import {
  useDeprecateInstantFlow, useInstantFlow, useInstantFlowOrgSetting, usePublishInstantFlow, useSetInstantFlowOrgSetting,
  useSyncInstantFlow,
} from '@/lib/sdk/instant-flow/queries';
import type { InstantFlowRecordStatus, InstantFlowSyncAction, InstantFlowVersion } from '@/lib/sdk/instant-flow/types';

const statusColors: Record<InstantFlowRecordStatus, string> = {
  DRAFT: 'yellow',
  PUBLISHED: 'green',
  DEPRECATED: 'gray',
};

const syncTitles: Record<InstantFlowSyncAction, (version: number) => string> = {
  created: (version) => `Created draft v${version}`,
  updated: (version) => `Updated draft v${version}`,
  unchanged: () => 'Already up to date',
};

const formatTime = (value?: string): string => (value ? new Date(value).toLocaleString() : '—');

const errorMessage = (error: unknown): string | undefined => (error instanceof Error ? error.message : undefined);

// One WhatsApp Flow serves instant tasks for every organization, so this page shows
// the same thing under any org. Super admin only.
export function InstantFlowPage() {
  const toast = useToast();
  const { data, isLoading } = useInstantFlow();
  const sync = useSyncInstantFlow();
  const publish = usePublishInstantFlow();
  const deprecate = useDeprecateInstantFlow();
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id ?? '';
  const orgSetting = useInstantFlowOrgSetting(orgId);
  const setOrgSetting = useSetInstantFlowOrgSetting(orgId);
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [deprecateTarget, setDeprecateTarget] = useState<InstantFlowVersion | null>(null);

  // Newest first on the page; the API lists oldest first.
  const flows = useMemo(() => [...(data?.flows ?? [])].reverse(), [data]);
  const newest = flows[0];
  const canPublish = newest?.status === 'DRAFT' && (data?.json_current ?? false);
  const replaced = flows.filter((flow) => flow.status === 'PUBLISHED');
  // What a launch opens: the published Flow; before the first publish, a draft in draft
  // mode; once a Flow has been published and none is now, nothing (the chat list).
  const everPublished = flows.some((flow) => flow.status !== 'DRAFT');
  const liveNote = replaced.length > 0
    ? 'Members get the published Flow.'
    : !everPublished && flows.some((flow) => flow.status === 'DRAFT')
      ? 'No Flow has been published yet, so members get the draft in draft mode. Use this to test.'
      : 'No Flow is live, so members get the chat task list.';

  const toggleOrg = (enabled: boolean) => {
    setOrgSetting.mutate(enabled, {
      onSuccess: () => toast({ status: 'success', title: enabled ? `Flow on for ${currentOrg?.name ?? 'this organization'}` : `Flow off for ${currentOrg?.name ?? 'this organization'}` }),
      onError: (error) => toast({ status: 'error', title: 'Could not change the setting', description: errorMessage(error) }),
    });
  };

  // Shown on the page rather than in a toast: Meta's validation errors are long and
  // need reading. Each action clears the others' errors, so only the latest shows.
  const failure = [
    { title: 'Could not sync the Flow', error: sync.error },
    { title: 'Could not publish the Flow', error: publish.error },
    { title: 'Could not deprecate the Flow', error: deprecate.error },
  ].find((entry) => entry.error !== null);
  const clearFailures = () => {
    sync.reset();
    publish.reset();
    deprecate.reset();
  };

  const runSync = () => {
    clearFailures();
    sync.mutate(undefined, {
      onSuccess: (result) => {
        toast({
          status: 'success',
          title: syncTitles[result.action](result.flow.version),
          description: result.action === 'unchanged' ? undefined : 'Test the draft, then publish it.',
        });
      },
    });
  };

  const runPublish = () => {
    clearFailures();
    publish.mutate(undefined, {
      onSuccess: (result) => {
        setIsPublishOpen(false);
        toast({
          status: 'success',
          title: `Published v${result.flow.version}`,
          description: result.deprecated.length > 0 ? `Deprecated ${result.deprecated.join(', ')}.` : undefined,
        });
      },
      onError: () => setIsPublishOpen(false),
    });
  };

  const runDeprecate = (flow: InstantFlowVersion) => {
    clearFailures();
    deprecate.mutate(flow.flow_id, {
      onSuccess: () => {
        setDeprecateTarget(null);
        toast({ status: 'success', title: `Deprecated v${flow.version}` });
      },
      onError: () => setDeprecateTarget(null),
    });
  };

  const columns: ColumnDef<InstantFlowVersion, unknown>[] = [
    {
      header: 'Flow', accessorKey: 'name', size: 300,
      cell: (info) => (
        <VStack align="flex-start" spacing={0}>
          <Text fontWeight="medium">v{info.row.original.version}</Text>
          <Text fontSize="sm" color="text-secondary">{info.row.original.name}</Text>
          <Text fontSize="xs" color="text-secondary">Meta id {info.row.original.flow_id}</Text>
        </VStack>
      ),
    },
    {
      header: 'Status', accessorKey: 'status', size: 200,
      cell: (info) => {
        const flow = info.row.original;
        const mismatch = flow.meta_status !== undefined && flow.meta_status !== flow.status;
        return (
          <VStack align="flex-start" spacing={1}>
            <Badge colorScheme={statusColors[flow.status]}>{flow.status}</Badge>
            {mismatch && <Badge colorScheme="orange">Meta: {flow.meta_status}</Badge>}
            {flow.meta_error && (
              <Text fontSize="xs" color="red.500" title={flow.meta_error} data-testid={`instant-flow-meta-error-${flow.flow_id}`}>
                Meta status unavailable
              </Text>
            )}
          </VStack>
        );
      },
    },
    {
      header: 'Created', accessorKey: 'created_at', size: 170,
      cell: (info) => <Text fontSize="sm" whiteSpace="nowrap">{formatTime(info.row.original.created_at)}</Text>,
    },
    {
      header: 'Published', accessorKey: 'published_at', size: 170,
      cell: (info) => <Text fontSize="sm" whiteSpace="nowrap">{formatTime(info.row.original.published_at)}</Text>,
    },
    {
      header: 'Deprecated', accessorKey: 'deprecated_at', size: 170,
      cell: (info) => <Text fontSize="sm" whiteSpace="nowrap">{formatTime(info.row.original.deprecated_at)}</Text>,
    },
    {
      header: '', id: 'actions', size: 120,
      cell: (info) => info.row.original.status === 'PUBLISHED' && (
        <Button
          size="sm" variant="outline" colorScheme="red"
          isDisabled={deprecate.isPending}
          data-testid={`instant-flow-deprecate-${info.row.original.flow_id}`}
          onClick={() => setDeprecateTarget(info.row.original)}
        >
          Deprecate
        </Button>
      ),
    },
  ];

  return (
    <PageContainer title="Instant Task Flow">
      <VStack spacing={4} align="stretch">
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          One WhatsApp Flow serves instant tasks for every organization. Sync puts the current Flow
          JSON in a draft while the live Flow keeps running; test the draft, then publish it.
        </Alert>
        <Box borderWidth="1px" borderRadius="md" p={4}>
          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <FormLabel htmlFor="instant-flow-org" mb={0}>
              <Text fontWeight="medium" color="text-primary" fontSize="sm">
                Use the Flow for {currentOrg?.name ?? 'this organization'}
              </Text>
              <Text fontSize="xs" color="text-secondary">
                The task-list buttons (new task, reminder, SHOW ALL) and /tasks send one Flow message
                instead of the chat lists. {liveNote}
              </Text>
              {orgSetting.isError && (
                <Text fontSize="xs" color="red.500" data-testid="instant-flow-org-error">
                  Could not load this setting: {errorMessage(orgSetting.error)}
                </Text>
              )}
            </FormLabel>
            <Switch
              id="instant-flow-org"
              data-testid="instant-flow-org-switch"
              colorScheme="brand"
              isChecked={orgSetting.data?.enabled ?? false}
              isDisabled={orgSetting.isLoading || orgSetting.isError || setOrgSetting.isPending}
              onChange={(event) => toggleOrg(event.target.checked)}
            />
          </FormControl>
        </Box>
        {!isLoading && flows.length === 0 && (
          <Alert status="warning" borderRadius="md" data-testid="instant-flow-none">
            <AlertIcon />
            There is no Flow yet. Sync to create the first draft.
          </Alert>
        )}
        {!isLoading && flows.length > 0 && !data?.json_current && (
          <Alert status="warning" borderRadius="md" data-testid="instant-flow-outdated">
            <AlertIcon />
            The Flow JSON has changed since v{newest?.version}. Sync to put it in a draft.
          </Alert>
        )}
        {failure && (
          <Alert status="error" borderRadius="md" alignItems="flex-start" data-testid="instant-flow-error">
            <AlertIcon />
            <VStack align="flex-start" spacing={1}>
              <Text fontWeight="medium">{failure.title}</Text>
              <Text fontSize="sm" wordBreak="break-word">{errorMessage(failure.error)}</Text>
            </VStack>
          </Alert>
        )}
        <HStack justify="flex-end">
          <Button
            variant="outline" size="sm"
            isLoading={sync.isPending}
            data-testid="instant-flow-sync"
            onClick={runSync}
          >
            Sync
          </Button>
          <Button
            colorScheme="brand" size="sm"
            isDisabled={!canPublish}
            title={canPublish ? undefined : 'Sync first: publish needs a draft that holds the current JSON'}
            isLoading={publish.isPending}
            data-testid="instant-flow-publish"
            onClick={() => setIsPublishOpen(true)}
          >
            Publish draft
          </Button>
        </HStack>
        <DataTable
          data={flows}
          columns={columns}
          isLoading={isLoading}
          getRowId={(flow) => flow.flow_id}
          emptyMessage="No instant-task Flow has been created."
        />
      </VStack>
      <ConfirmDialog
        isOpen={isPublishOpen}
        onClose={() => setIsPublishOpen(false)}
        onConfirm={runPublish}
        title={`Publish v${newest?.version ?? ''}?`}
        message={
          'Publishing freezes the draft\'s JSON for good.' +
          (replaced.length > 0
            ? ` ${replaced.map((flow) => `v${flow.version}`).join(', ')} will be deprecated, and templates whose Flow button names it need pointing at the new Flow.`
            : '')
        }
        confirmText="Publish"
        isLoading={publish.isPending}
        colorScheme="orange"
      />
      <ConfirmDialog
        isOpen={deprecateTarget !== null}
        onClose={() => setDeprecateTarget(null)}
        onConfirm={() => deprecateTarget && runDeprecate(deprecateTarget)}
        title={`Deprecate v${deprecateTarget?.version ?? ''}?`}
        message="A deprecated Flow stops for good and can't be restored. Instant tasks can't open it any more, and members get the chat task list until a new Flow is published."
        confirmText="Deprecate"
        isLoading={deprecate.isPending}
        colorScheme="red"
      />
    </PageContainer>
  );
}
